import { SessionQuery } from './../../../../core/state/session/session.query';
import { Category, CategoryLabel, ICategory } from './categories.model';
import { CategoriesStore } from './categories.store';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { tap, filter, take, switchMapTo } from 'rxjs/operators';
import { ID, applyTransaction } from '@datorama/akita';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CategoryService {
    constructor(
        private config: AppConfigQuery,
        private categoriesStore: CategoriesStore,
        private sessionQuery: SessionQuery,
        private http: HttpClient
    ) {
        this.sessionQuery.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                take(1),
                switchMapTo(this.get())
            )
            .subscribe();
    }

    get() {
        this.setLoading('categories');
        return this.http
            .get<Array<Category>>(`${this.config.get().categoriesBaseUrl}/categories`)
            .pipe(
                tap((categories) => {
                    applyTransaction(() => {
                        this.categoriesStore.upsertMany(this._processMultipe(categories));
                        this.setLoading('categories', false);
                    });
                })
            );
    }

    getSingle(categoryId: ID) {
        return this.http
            .get<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/${categoryId}`
            )
            .pipe(
                tap((category) => {
                    this.categoriesStore.upsert(
                        [categoryId],
                        this._processSingle(category)
                    );
                })
            );
    }

    // categories
    upsertCategory(category: Category): Observable<Category> {
        if (category.id) {
            return this._editCategory(category, category.id);
        }
        return this._addCategory(category);
    }

    private _addCategory(category: Partial<Category>): Observable<Category> {
        return this.http
            .post<Category>(`${this.config.get().categoriesBaseUrl}/categories`, category)
            .pipe(
                tap((newCategory) =>
                    this.categoriesStore.add(this._processSingle(newCategory), {
                        prepend: true
                    })
                )
            );
    }

    private _editCategory(category: Category, categoryId: ID): Observable<Category> {
        return this.http
            .patch<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/${categoryId}`,
                category
            )
            .pipe(
                tap((category) =>
                    this.categoriesStore.upsert(categoryId, this._processSingle(category))
                )
            );
    }

    updateCategory(id, category: Partial<Category>) {
        this.categoriesStore.update(id, category);
    }

    removeCategory(categoryId: ID) {
        return this.http
            .delete<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/${categoryId}`
            )
            .pipe(tap(() => this.categoriesStore.remove(categoryId)));
    }

    /** Clone category - create a new category by using an existing category as template.
     * @param categoryId - category id
     * @param name - name for the new category
     * @returns - observable for request
     */
    cloneCategory(category: Category) {
        return this.http
            .post<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/override/${
                    category.id
                }?name=${category.name}`,
                {}
            )
            .pipe(
                tap((newCategory) =>
                    this.categoriesStore.add(this._processSingle(newCategory), {
                        prepend: true
                    })
                )
            );

        // TODO: implement clone feature - SWIM-3220
    }

    // category labels
    upsertLabel(
        categoryId: ID,
        parentId: ID,
        label: CategoryLabel
    ): Observable<Category> {
        if (label.id) {
            return this.editLabel(categoryId, label);
        }
        return this.addLabel(categoryId, parentId, label);
    }

    addLabel(
        categoryId: ID,
        parentLabelId: ID,
        label: Partial<CategoryLabel>
    ): Observable<Category> {
        const payload: Partial<CategoryLabel> = { categoryId, parentLabelId, ...label };
        return this.http
            .post<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/labels/add`,
                payload
            )
            .pipe(
                tap((updatedCategory) =>
                    this.categoriesStore.update(
                        categoryId,
                        this._processSingle(updatedCategory)
                    )
                )
            );
    }

    editLabel(categoryId: ID, label: Partial<CategoryLabel>): Observable<Category> {
        const payload = {
            categoryId,
            labelId: label.id,
            name: label.name
        };

        return this.http
            .patch<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/labels/edit`,
                payload
            )
            .pipe(
                tap((updatedCategory) =>
                    this.categoriesStore.update(
                        categoryId,
                        this._processSingle(updatedCategory)
                    )
                )
            );
    }

    removeLabel(categoryId: ID, labelId: ID): Observable<Category> {
        const payload = { categoryId, labelId };
        return this.http
            .patch<Category>(
                `${this.config.get().categoriesBaseUrl}/categories/labels/remove`,
                payload
            )
            .pipe(
                tap((updatedCategory) =>
                    this.categoriesStore.update(
                        categoryId,
                        this._processSingle(updatedCategory)
                    )
                )
            );
    }

    private _processSingle(category: ICategory, parent = null): Category {
        return this._processMultipe([category])[0];
    }

    private _processMultipe(
        data,
        parent: Partial<ICategory> | null = null,
        rootId: ID | undefined = undefined
    ): Category[] {
        return data.map((i: ICategory) => {
            const item: Partial<CategoryLabel> = { ...i };
            if (item.labels === undefined) item.labels = [];
            if (parent !== null) {
                item.parentLabelId = parent.id;
                if (rootId) item.categoryId = rootId;
                item.categoryScope = parent.categoryScope;
            }
            if (item.labels)
                item.labels = this._processMultipe(
                    item.labels,
                    item,
                    rootId ? rootId : item.id
                );
            return item;
        });
    }

    setLoading(request: string, isLoading: boolean = true) {
        this.categoriesStore.update((state) => ({
            ...state,
            ui: {
                ...state.ui,
                [request]: {
                    isLoading
                }
            }
        }));
    }
}
